// ADR 23.9.26y §6–§7 — render-nothing, mounted once in the shell.
//
// Two duties. The reconcile runs on every signed-in page, not only on
// `/account`, and it is keyed on the **user id** rather than on mount, so a
// second learner signing in on an already-open page gets their own turn — the
// shared-browser backstop. And the sign-out listener is registered
// **unconditionally**, behind none of the flag, verification or support gates,
// so a flag switched off mid-session cannot leave a subscription behind.
//
// The flow and the adapter arrive by `import()`: this file is in the entry
// chunk, which sat at its 576 kB ceiling (`bundleBudget.ts`), and the worker
// precaches every chunk, so the sign-out path still works offline.

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuthSession } from '../../auth/session'
import { SIGNED_OUT_EVENT } from '../../auth/tokenStore'
import { pushSupport, readPushEnv } from './pushSupport'

function useSignedOutUnsubscribe(): void {
  useEffect(() => {
    const onSignedOut = () => {
      void import('./browserPush').then(({ unsubscribeLocally }) => unsubscribeLocally()).catch(() => undefined)
    }
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut)
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut)
  }, [])
}

export function ReminderReconcile(): null {
  const { i18n } = useTranslation()
  const { status, userId, flags, emailVerified } = useAuthSession()
  const gated = status === 'signedIn' && flags?.web_push_reminders_enabled === true && emailVerified
  const reconcileFor = gated ? userId : null

  useSignedOutUnsubscribe()
  useEffect(() => {
    if (reconcileFor === null || pushSupport(readPushEnv()) !== 'supported') return
    void Promise.all([import('./reminderFlow'), import('./browserPush')])
      .then(([flow, push]) => flow.reconcileOnce(reconcileFor, push.browserPushPorts(() => i18n.language)))
      .catch(() => undefined)
  }, [reconcileFor, i18n])
  return null
}
